/**
 * 편집기의 유일한 상태 전이. 행 70개짜리 견적을 키 입력마다 통째로 복사하지 않도록
 * 건드린 공종·행만 새로 만들고 나머지는 참조를 그대로 넘긴다 — memo 가 살아 있어야 안 렉린다.
 */

import { QuoteAmounts } from "../action-types";
import { CalcQuoteInput, CalcQuoteResult } from "../calc";
import { m2ToPyeong, pyeongToM2 } from "../area-unit";
import { areaOf, editorHeadOf, mgmtRateOf } from "../edit-head";
import { evalQtyExpr, isExpr, QTY_EXPR_MAX_LENGTH } from "../qty-expr";
import { convertUnitPrice, isSaleFromMargin, marginOf, saleFromMargin } from "../margin";
import { keepsWithoutArea, trustedQtyExpr } from "../qty-expr-trust";
import { QuoteDraftInput, QuoteItemInput } from "../schema";
import { QuoteDetail, QuoteItem, QuoteStatus } from "../types";
import { DEFAULT_VAT_MODE, VatMode } from "../../money/vat";
import {
  CellPatch,
  EditorAction,
  EditorItem,
  EditorItemPatch,
  EditorPhasePlan,
  EditorState,
  EditorTrade,
  FillField,
  FilledItemInput,
  ImportTradeInput,
  ItemRef,
} from "./types";
import { TradeCode, TradeGroup } from "../../template/trades";
import { TemplateItem } from "../../template/types";
import { isMarginBp, splitSale } from "../../template/price-margin";

// 머리 칸 변환은 서버 액션도 쓰므로 ui 밖(edit-head)에 둔다 — 기존 import 자리를 위해 다시 내보낸다.
export { areaOf, editorHeadOf, mgmtRateOf };

let seq = 0;
/** React key 로만 쓴다. 저장할 때는 버려지므로 서버 id 와 맞출 필요가 없다. */
export const nextKey = (): string => {
  seq += 1;
  return `k${seq}`;
};

export const emptyItem = (): EditorItem => ({
  key: nextKey(),
  scope: "",
  space_code: "",
  item: "",
  unit: "",
  qty: 0,
  material_unit_price: 0,
  labor_unit_price: 0,
  note: "",
  children: [],
  suggested: false,
});

// 템플릿 수량식 → 수량: 평수로 풀리면 그 값, 평수만 없으면 식을 남기고 0, 숫자면 그 수량.
export const templateQtyOf = (
  raw: string | undefined,
  area: number | null,
  areaM2?: number | null
): Pick<EditorItem, "qty" | "qty_expr"> => {
  const text = (raw ?? "").trim();
  if (text === "" || text.length > QTY_EXPR_MAX_LENGTH) return { qty: 0 };
  const result = evalQtyExpr(text, { area_pyeong: area, area_m2: areaM2 });
  if (!isExpr(text)) return { qty: result.ok ? result.value : 0 };
  if (result.ok) return { qty: result.value, qty_expr: text };
  return result.kind === "no_area" && keepsWithoutArea(text) ? { qty: 0, qty_expr: text } : { qty: 0 };
};

const fromTemplateItem = (template: TemplateItem, area: number | null, areaM2?: number | null): EditorItem => {
  const children = (template.children ?? []).map((child) => fromTemplateItem(child, area, areaM2));
  return {
    ...emptyItem(),
    // 시드 템플릿은 규격이 null 인 줄이 있다(타입은 string) — 입구에서 빈 문자열로 모은다.
    scope: str(template.scope),
    // 템플릿은 공간을 모른다 — 현장마다 다르므로 견적에서 고른다.
    space_code: "",
    item: str(template.item),
    unit: str(template.unit),
    // SET 부모의 수량은 자식 합이라 식을 두지 않는다.
    ...(children.length > 0 ? {} : templateQtyOf(template.qty_expr, area, areaM2)),
    children,
  };
};

/** 템플릿에서 가져온 항목. 단가는 비운다 — 이번 견적에서 다시 정한다. */
export const templateItemsToRows = (
  items: TemplateItem[],
  area: number | null = null,
  areaM2?: number | null
): EditorItem[] => items.map((item) => fromTemplateItem(item, area, areaM2));

/* ── 초기 상태 ─────────────────────────────────────────────── */

const toRow = (row: QuoteItem, all: QuoteItem[], area: number | null, areaM2?: number | null): EditorItem => {
  const children = all
    .filter((i) => i.parent_id === row.id)
    .map((i) => toRow(i, all, area, areaM2));
  // SET 부모는 식을 두지 않고, 잎은 qty 를 만드는 식만 살린다.
  const qtyExpr = children.length > 0 ? null : trustedQtyExpr(row.qty, row.qty_expr, area, areaM2);
  return {
    key: nextKey(),
    scope: row.scope,
    space_code: row.space_code ?? "",
    item: row.item,
    unit: row.unit,
    qty: row.qty,
    ...(qtyExpr ? { qty_expr: qtyExpr } : {}),
    material_unit_price: row.material_unit_price,
    labor_unit_price: row.labor_unit_price,
    cost_unit_price: children.length > 0 ? null : costOf(row.cost_unit_price),
    base_margin_bp: children.length > 0 ? null : marginBpValue(row.base_margin_bp),
    note: row.note,
    children,
    suggested: false,
  };
};

/**
 * 되살린 상태의 React key 를 전부 새로 붙인다.
 *
 * `nextKey` 의 카운터는 모듈 전역이고 새로고침마다 0 부터 센다. 지난 세션에서
 * 쓰던 키(`k1`…`k30`)를 그대로 들여오면, 이번 세션이 `k21` 을 새로 발급하는
 * 순간 같은 키가 두 줄이 된다 — 행을 `key ===` 로 찾는 `SET_ITEM`·`REMOVE_ITEM`
 * 이 엉뚱한 줄을 같이 고치거나 지운다.
 */
const rekey = (state: EditorState): EditorState => {
  const rekeyItem = (item: EditorItem): EditorItem => ({
    ...item,
    key: nextKey(),
    children: item.children.map(rekeyItem),
  });

  return {
    ...state,
    condition_keys: state.conditions.map(() => nextKey()),
    trades: state.trades.map((trade) => ({
      ...trade,
      key: nextKey(),
      items: trade.items.map(rekeyItem),
    })),
  };
};

/* ── 이어쓰기 초안 되살리기 ───────────────────────────────────── */

/**
 * 초안은 **저장소에 굳어 있던 남의 모양**이다.
 *
 * 머리 몇 칸만 보고 통째로 `as EditorState` 하면, 그 뒤에 모양이 한 번이라도
 * 바뀌는 순간 편집기가 죽는다 — 실제로 두 자리가 그랬다. 항목에 `children` 이
 * 없으면 `rekey` 가 `undefined.map` 으로 터지고, 공간 축이 생기기 전의 초안이면
 * 저장 직전 `isBlank` 가 `undefined.trim()` 으로 터진다. 인메모리라 지금은 재시작
 * 때 초안이 사라져 안 보이지만, `quote_draft` 가 표로 살아남는 순간 **스키마를
 * 고칠 때마다** 그 시점에 편집 중이던 사람들이 흰 화면을 본다.
 *
 * 그래서 읽을 때 한 칸씩 **다시 짓는다.** 모르는 값은 기본값으로 떨어지고,
 * 못 읽는 줄은 빠질지언정 편집기는 뜬다.
 */
const str = (value: unknown, fallback = ""): string =>
  typeof value === "string" ? value : fallback;

const num = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

// 원가 단가로 쓸 수 있는 값(0 이상 안전정수)만 받고 나머지는 미입력(null).
const costOf = (value: unknown): number | null =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : null;

// 기준 마진 칸 — 범위 안 정수(만분율)만 받는다.
const marginBpValue = (value: unknown): number | null => (isMarginBp(value) ? value : null);

// 부가세 방식 칸 — 아는 값만 받는다.
const vatModeValue = (value: unknown): VatMode | null =>
  value === "INCL" || value === "EXCL" ? value : null;

const normalizeItem = (raw: unknown): EditorItem | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const rec = raw as Record<string, unknown>;
  const children = Array.isArray(rec.children)
    ? rec.children.map(normalizeItem).filter((c): c is EditorItem => c !== null)
    : [];
  return {
    // 키는 늘 새로 붙인다 — `RESTORE` 가 어차피 `rekey()` 로 전부 갈아 끼우므로
    // 저장된 키를 살리는 척하면 읽는 사람만 헷갈린다.
    key: nextKey(),
    scope: str(rec.scope),
    space_code: str(rec.space_code),
    item: str(rec.item),
    unit: str(rec.unit),
    qty: num(rec.qty),
    // SET 부모의 수량은 자식 합이라 식을 두지 않는다.
    ...(children.length === 0 && str(rec.qty_expr).trim() !== ""
      ? { qty_expr: str(rec.qty_expr) }
      : {}),
    material_unit_price: num(rec.material_unit_price),
    labor_unit_price: num(rec.labor_unit_price),
    // 원가가 생기기 전 초안·SET 부모는 미입력(null).
    cost_unit_price: children.length > 0 ? null : costOf(rec.cost_unit_price),
    base_margin_bp: children.length > 0 ? null : marginBpValue(rec.base_margin_bp),
    note: str(rec.note),
    children,
    suggested: rec.suggested === true,
  };
};

const normalizeTrade = (raw: unknown): EditorTrade | null => {
  if (typeof raw !== "object" || raw === null) return null;
  const rec = raw as Record<string, unknown>;
  const trade = str(rec.trade);
  // 공종 코드가 없으면 저장할 수도, 화면에 둘 수도 없다.
  if (trade === "") return null;
  return {
    key: nextKey(),
    group: rec.group === "STYLE" ? "STYLE" : "BASIC",
    trade,
    name: str(rec.name) || trade,
    sort_order: num(rec.sort_order),
    condition_text: str(rec.condition_text),
    collapsed: rec.collapsed === true,
    items: Array.isArray(rec.items)
      ? rec.items.map(normalizeItem).filter((i): i is EditorItem => i !== null)
      : [],
  };
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const isoOr = (value: unknown): string => {
  const raw = str(value);
  return ISO_DATE.test(raw) ? raw : "";
};

/**
 * 자동저장해 둔 일정을 되살린다.
 *
 * **이 기능 전에 저장된 초안에는 `schedule` 이 아예 없다.** 없으면 빈 일정으로
 * 떨어져야지, 되살리기 자체가 실패하면 그날 친 견적을 통째로 잃는다.
 */
const normalizeSchedule = (value: unknown): EditorState["schedule"] => {
  const empty: EditorState["schedule"] = {
    expected_start_date: "",
    work_saturday: false,
    work_sunday: false,
    work_holiday: false,
    holidays: null,
    plans: [],
  };
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return empty;
  }
  const rec = value as Record<string, unknown>;

  const plans = Array.isArray(rec.plans)
    ? rec.plans
        .map((row): EditorPhasePlan | null => {
          if (typeof row !== "object" || row === null) return null;
          const r = row as Record<string, unknown>;
          const code = str(r.phase_code);
          if (code === "") return null;
          const days = Number(r.duration_days);
          return {
            phase_code: code,
            duration_days:
              Number.isFinite(days) && days > 0 ? Math.floor(days) : 0,
            start_date: isoOr(r.start_date),
            end_date: isoOr(r.end_date),
            ...restoredBasisOf(r),
          };
        })
        .filter((p): p is EditorPhasePlan => p !== null)
    : [];

  return {
    expected_start_date: isoOr(rec.expected_start_date),
    work_saturday: rec.work_saturday === true,
    work_sunday: rec.work_sunday === true,
    work_holiday: rec.work_holiday === true,
    holidays: Array.isArray(rec.holidays)
      ? rec.holidays.map(isoOr).filter((d) => d !== "")
      : null,
    plans,
    process_standard_version_id: str(rec.process_standard_version_id) || null,
  };
};

// 양수가 아니면 null — 되살린 초안 근거 숫자.
const positiveOrNull = (value: unknown): number | null => {
  const n = typeof value === "number" ? value : Number.NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

// 되살린 줄의 초안 근거 — 값이 있는 칸만 싣는다(근거 없는 옛 초안은 모양이 그대로).
const restoredBasisOf = (r: Record<string, unknown>): Partial<EditorPhasePlan> => {
  const crew = positiveOrNull(r.crew_size);
  const basis: Partial<EditorPhasePlan> = {
    standard_item_code: str(r.standard_item_code) || null,
    quantity_m2: positiveOrNull(r.quantity_m2),
    crew_size: crew !== null && Number.isInteger(crew) ? crew : null,
    draft_days: positiveOrNull(r.draft_days),
  };
  return Object.fromEntries(Object.entries(basis).filter(([, v]) => v !== null)) as Partial<EditorPhasePlan>;
};

// 초안의 면적 — m² 키가 있으면 그것이 원본, 평만 있으면 불러온 견적과 같은 평일 때 그 견적의 m² 를, 아니면 평×3.305785.
const restoredAreaOf = (
  rec: Record<string, unknown>,
  loadedArea: number | null,
  loadedAreaM2: number | null
): { area_pyeong: number | null; area_m2: number | null } => {
  if ("area_m2" in rec) {
    const m2 = areaOf(rec.area_m2);
    return { area_m2: m2, area_pyeong: m2 === null ? null : m2ToPyeong(m2) };
  }
  const pyeong = areaOf("area_pyeong" in rec ? rec.area_pyeong : loadedArea);
  const sameAsLoaded = pyeong !== null && pyeong === areaOf(loadedArea) && areaOf(loadedAreaM2) !== null;
  return { area_pyeong: pyeong, area_m2: sameAsLoaded ? areaOf(loadedAreaM2) : pyeongToM2(pyeong) };
};

// 평수 키가 없는 옛 초안은 `loadedArea`(불러온 견적의 평수)를 쓴다 — null 로 채우면 저장이 서버 평수를 지운다 · 부가세 방식도 같다(`loadedVatMode`).
export const restoredStateOf = (
  data: unknown,
  loadedArea: number | null = null,
  loadedVatMode: VatMode = DEFAULT_VAT_MODE,
  loadedAreaM2: number | null = null
): EditorState | null => {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    return null;
  }
  const rec = data as Record<string, unknown>;
  // 이 셋이 없으면 편집기 상태가 아니다. 나머지는 기본값으로 채운다.
  if (typeof rec.title !== "string") return null;
  if (typeof rec.mgmt_fee_pct !== "string") return null;
  if (!Array.isArray(rec.trades)) return null;

  const conditions = Array.isArray(rec.conditions)
    ? rec.conditions.map((line) => str(line))
    : [];
  const area = restoredAreaOf(rec, loadedArea, loadedAreaM2);

  return {
    title: rec.title,
    mgmt_fee_pct: rec.mgmt_fee_pct,
    rounding:
      typeof rec.rounding === "number" && Number.isFinite(rec.rounding)
        ? rec.rounding
        : null,
    conditions,
    // 길이가 어긋나면 화면이 줄을 잘못 짝짓는다. 여기서 다시 맞춘다.
    condition_keys: conditions.map(() => nextKey()),
    schedule: normalizeSchedule(rec.schedule),
    extra_notice: str(rec.extra_notice),
    area_pyeong: area.area_pyeong,
    area_m2: area.area_m2,
    vat_mode: vatModeValue(rec.vat_mode) ?? loadedVatMode,
    trades: rec.trades
      .map(normalizeTrade)
      .filter((t): t is EditorTrade => t !== null),
    dirty: true,
    saved: null,
  };
};

export const initEditorState = (detail: QuoteDetail): EditorState => {
  const { quote, trades, items, plans } = detail;
  const head = editorHeadOf(quote);
  const area = head.area_pyeong;
  const areaM2 = head.area_m2 ?? null;

  return {
    ...head,
    vat_mode: quote.vat_mode ?? DEFAULT_VAT_MODE,
    condition_keys: quote.conditions.map(() => nextKey()),
    trades: trades.map((trade) => {
      const own = items.filter((i) => i.quote_trade_id === trade.id);
      return {
        key: nextKey(),
        group: trade.group,
        trade: trade.trade,
        name: trade.name,
        sort_order: trade.sort_order,
        condition_text: trade.condition_text,
        collapsed: false,
        items: own.filter((i) => !i.parent_id).map((i) => toRow(i, own, area, areaM2)),
      };
    }),
    schedule: {
      expected_start_date: quote.expected_start_date ?? "",
      work_saturday: quote.work_saturday,
      work_sunday: quote.work_sunday,
      work_holiday: quote.work_holiday,
      holidays: quote.schedule_holidays ? [...quote.schedule_holidays] : null,
      plans: plans.map((p) => ({
        phase_code: p.phase_code,
        duration_days: p.duration_days,
        start_date: p.start_date ?? "",
        end_date: p.end_date ?? "",
        standard_item_code: p.standard_item_code ?? null,
        quantity_m2: p.quantity_m2 ?? null,
        crew_size: p.crew_size ?? null,
        draft_days: p.draft_days ?? null,
      })),
      process_standard_version_id: quote.process_standard_version_id ?? null,
    },
    dirty: false,
    saved: {
      subtotal_basic: quote.subtotal_basic,
      subtotal_style: quote.subtotal_style,
      mgmt_fee: quote.mgmt_fee,
      gross: quote.gross,
      rounding_adjustment: quote.rounding_adjustment ?? 0,
      total: quote.total,
      ...(quote.supply_amount !== undefined ? { supply_amount: quote.supply_amount } : {}),
      ...(quote.vat_amount !== undefined ? { vat_amount: quote.vat_amount } : {}),
    },
  };
};

/* ── 파생값 ────────────────────────────────────────────────── */

/**
 * 관리비율 입력의 오류 문구. 없으면 null.
 *
 * `mgmtRateOf` 가 범위 밖 값을 조용히 0·100% 로 자르므로, 사용자는 자기가 친 값이
 * 안 쓰였다는 것을 모른다. 화면은 이 문구로 알리고 저장을 막는다.
 */
export const mgmtPctError = (pct: string): string | null => {
  const raw = pct.trim();
  // 입력 중간값("4." · "")은 오류로 치지 않는다 — 치는 동안 빨간 줄이 깜빡이면 거슬린다.
  if (raw === "" || raw === ".") return null;

  const n = Number(raw);
  if (!Number.isFinite(n)) return "관리비율은 숫자여야 합니다";
  if (n < 0) return "관리비율은 0% 이상이어야 합니다";
  if (n > 100) return "관리비율은 100% 를 넘을 수 없습니다";
  return null;
};

/**
 * **저장할 때의** 관리비율 검사. 치는 동안보다 한 칸 엄격하다.
 *
 * 빈 칸을 오류로 안 치는 것은 **치는 동안**의 이야기다. 그대로 저장까지 가면
 * `mgmtRateOf("")` 가 0 을 돌려주어 **관리비 0% 로 굳는다** — 5,000만원 견적에서
 * 4% 면 200만원이 소리 없이 사라지고, 그 상태로 고객에게 나간다.
 *
 * 0% 견적이 없는 것은 아니므로 막는 대신 **0 을 적으라고** 말한다. 회사 기본값을
 * 받는 설정 화면도 같은 이유로 빈 칸을 「안 고침」 으로 따로 다룬다.
 */
export const mgmtPctSaveError = (pct: string): string | null => {
  const raw = pct.trim();
  if (raw === "" || raw === ".") {
    return "관리비율을 입력해 주세요 — 안 받으려면 0 을 적으세요";
  }
  return mgmtPctError(raw);
};

// 이 편집 상태의 부가세 방식 — 칸이 없으면(옛 상태) 포함.
export const vatModeOf = (state: Pick<EditorState, "vat_mode">): VatMode =>
  state.vat_mode ?? DEFAULT_VAT_MODE;

export const calcInputOf = (state: EditorState): CalcQuoteInput => ({
  mgmt_fee_rate: mgmtRateOf(state.mgmt_fee_pct),
  rounding_adjustment: state.rounding,
  vat_mode: vatModeOf(state),
  trades: state.trades.map((t) => ({ group: t.group, items: t.items })),
});

// 계산 결과 → 합계 패널·저장 응답과 같은 모양의 금액(공급가·부가세 포함).
export const amountsOfCalc = (result: CalcQuoteResult): QuoteAmounts => ({
  subtotal_basic: result.subtotal_basic,
  subtotal_style: result.subtotal_style,
  mgmt_fee: result.mgmt_fee,
  gross: result.gross,
  rounding_adjustment: result.rounding_adjustment,
  total: result.total,
  supply_amount: result.supply_amount,
  vat_amount: result.vat_amount,
});

// 줄의 판매 단가(자재 + 노무) — 마진·원가 칸이 기준으로 쓴다.
export const saleUnitOf = (item: Pick<EditorItem, "material_unit_price" | "labor_unit_price">): number =>
  item.material_unit_price + item.labor_unit_price;

// 줄 마진(원가 대비 비율) · SET 부모·원가 0/없음이면 null.
export const itemMarginOf = (item: EditorItem, vatMode: VatMode): number | null =>
  item.children.length > 0 ? null : marginOf(saleUnitOf(item), item.cost_unit_price, vatMode);

// 마진 칸을 고칠 수 있는가 — 원가가 있고 SET 부모가 아니다(자재·노무를 나눈 줄은 지금 비율로 나눈다).
export const marginEditable = (item: EditorItem): boolean =>
  item.children.length === 0 && (item.cost_unit_price ?? 0) > 0;

// 계산 판매 단가 = 원가 × (1 + 기준 마진) — 원가·기준 마진이 없거나 SET 부모면 null.
export const calcSaleOf = (item: EditorItem, vatMode: VatMode): number | null => {
  const cost = item.cost_unit_price ?? 0;
  const bp = item.base_margin_bp ?? null;
  if (item.children.length > 0 || cost <= 0 || !isMarginBp(bp)) return null;
  try {
    return saleFromMargin(cost, bp / 10_000, vatMode);
  } catch {
    return null;
  }
};

// 판매가를 직접 입력했는가 — 계산값이 있고 지금 판매 단가가 새 계산값·#198 이전 원 단위 계산값 둘 다와 다르다.
export const isSaleOverridden = (item: EditorItem, vatMode: VatMode): boolean => {
  const calc = calcSaleOf(item, vatMode);
  if (calc === null) return false;
  return !isSaleFromMargin(saleUnitOf(item), item.cost_unit_price ?? 0, (item.base_margin_bp ?? 0) / 10_000, vatMode);
};

// 판매 합계를 그 줄의 지금 자재·노무 비율로 나눈다(합이 정확하다 · 노무만 쓴 줄은 노무, 아니면 자재).
const splitByRow = (
  item: Pick<EditorItem, "material_unit_price" | "labor_unit_price">,
  sale: number
): { material_unit_price: number; labor_unit_price: number } => {
  const { material, labor } = splitSale(sale, item.material_unit_price, item.labor_unit_price);
  return { material_unit_price: material, labor_unit_price: labor };
};

// 부가세 방식을 바꿀 수 있는 견적인가 — 작성 중만.
export const vatModeSwitchable = (status: QuoteStatus): boolean => status === "DRAFT";

// 전환 확인 창의 「단가 n개」 — 바뀔(0 이 아닌) 자재·노무 단가 칸 수.
export const vatSwitchPriceCount = (state: EditorState): number => {
  let count = 0;
  const visit = (item: EditorItem): void => {
    if (item.children.length > 0) {
      item.children.forEach(visit);
      return;
    }
    if (item.material_unit_price > 0) count += 1;
    if (item.labor_unit_price > 0) count += 1;
  };
  state.trades.forEach((t) => t.items.forEach(visit));
  return count;
};

/** 손대지 않은 빈 행. 저장할 때 조용히 버린다 — 이름 없는 행은 zod 가 막는다. */
/**
 * 아무것도 안 적힌 줄인가. 저장소의 `isBlankItem` 과 **같은 조건이어야 한다** —
 * 한쪽만 넓으면 화면에 남아 있는 줄이 저장 때 사라진다.
 */
const isBlank = (item: EditorItem): boolean =>
  item.item.trim().length === 0 &&
  item.scope.trim().length === 0 &&
  item.unit.trim().length === 0 &&
  // `space_code` 만 `?? ""` 를 두면 같은 줄의 다른 칸은 여전히 터진다 — 실제로
  // 지키는 것은 `restoredStateOf` 다. 여기서는 다른 칸과 같은 모양으로 둔다.
  item.space_code.trim().length === 0 &&
  item.note.trim().length === 0 &&
  item.qty === 0 &&
  item.material_unit_price === 0 &&
  item.labor_unit_price === 0 &&
  (item.cost_unit_price ?? null) === null &&
  item.children.length === 0;

// 잎의 계산되는 식만 싣는다 — 평수만 없어 못 푼 식은 `trustedQtyExpr` 처럼 문법만 보고 남긴다.
const draftQtyExpr = (item: EditorItem, area: number | null, areaM2?: number | null): string | null => {
  const text = (item.qty_expr ?? "").trim();
  if (item.children.length > 0 || text === "" || text.length > QTY_EXPR_MAX_LENGTH) return null;
  const result = evalQtyExpr(text, { area_pyeong: area, area_m2: areaM2 });
  if (result.ok) return text;
  return result.kind === "no_area" && keepsWithoutArea(text) ? text : null;
};

const toDraftItem = (item: EditorItem, area: number | null, areaM2?: number | null): QuoteItemInput => ({
  scope: item.scope.trim(),
  space_code: item.space_code || null,
  item: item.item.trim(),
  unit: item.unit.trim(),
  qty: item.qty,
  qty_expr: draftQtyExpr(item, area, areaM2),
  material_unit_price: item.material_unit_price,
  labor_unit_price: item.labor_unit_price,
  cost_unit_price: item.children.length > 0 ? null : item.cost_unit_price ?? null,
  base_margin_bp: item.children.length > 0 ? null : item.base_margin_bp ?? null,
  note: item.note.trim(),
  children: item.children.filter((c) => !isBlank(c)).map((c) => toDraftItem(c, area, areaM2)),
});

/** 편집기 상태의 m² — 옛 상태(m² 칸 없음)는 평×3.305785. */
export const stateAreaM2 = (state: Pick<EditorState, "area_pyeong" | "area_m2">): number | null =>
  state.area_pyeong === null ? null : (state.area_m2 ?? pyeongToM2(state.area_pyeong));

export const toDraftInput = (state: EditorState): QuoteDraftInput => ({
  title: state.title.trim(),
  mgmt_fee_rate: mgmtRateOf(state.mgmt_fee_pct),
  rounding_adjustment: state.rounding,
  conditions: state.conditions.map((c) => c.trim()).filter((c) => c.length > 0),
  extra_notice: state.extra_notice.trim(),
  area_pyeong: state.area_pyeong,
  // 면적 원본(m²) — 옛 상태(m² 없음)는 평에서 계산해 보낸다.
  area_m2: stateAreaM2(state),
  vat_mode: vatModeOf(state),
  /**
   * 목차 순서는 **공종 표가 정한 자리**를 그대로 굳힌다.
   *
   * 예전에는 화면 순서(`index + 1`)로 굳혔는데, 그러면 저장할 때마다 1..N 으로
   * 다시 매겨진다. 그 뒤 공종을 추가하면 표의 자리(예: 5)와 굳어 버린 1..N 이
   * 한 축에서 비교되어 **엉뚱한 자리에 끼어든다** — 목차가 틀린 견적서가 나간다.
   */
  trades: state.trades.map((trade) => ({
    group: trade.group,
    trade: trade.trade,
    name: trade.name.trim(),
    sort_order: trade.sort_order,
    condition_text: trade.condition_text.trim(),
    items: trade.items.filter((i) => !isBlank(i)).map((i) => toDraftItem(i, state.area_pyeong, stateAreaM2(state))),
  })),
  schedule: {
    expected_start_date: state.schedule.expected_start_date || null,
    work_saturday: state.schedule.work_saturday,
    work_sunday: state.schedule.work_sunday,
    work_holiday: state.schedule.work_holiday,
    holidays: state.schedule.holidays,
    /**
     * 며칠을 안 채운 줄은 보내지 않는다 — 저장소도 거르지만, 여기서 걸러 두면
     * 「빈 줄이 굳지 않는다」 가 화면 쪽에서도 한 번 더 지켜진다.
     */
    phases: state.schedule.plans
      .filter((p) => p.duration_days >= 1)
      .map((p) => ({
        phase_code: p.phase_code,
        duration_days: p.duration_days,
        start_date: p.start_date || null,
        end_date: p.end_date || null,
        note: "",
        standard_item_code: p.standard_item_code ?? null,
        quantity_m2: p.quantity_m2 ?? null,
        crew_size: p.crew_size ?? null,
        draft_days: p.draft_days ?? null,
      })),
    process_standard_version_id: state.schedule.process_standard_version_id ?? null,
  },
});

/* ── 전이 ──────────────────────────────────────────────────── */

const withSchedule = (
  state: EditorState,
  patch: Partial<EditorState["schedule"]>
): EditorState => ({
  ...state,
  schedule: { ...state.schedule, ...patch },
  dirty: true,
});

/**
 * 공정 줄 하나를 고친다. **없으면 만든다** — 공정 줄은 견적의 공종에서 파생되므로
 * 처음 타이핑하는 순간까지 상태에 행이 없다.
 */
const upsertPlan = (
  plans: EditorPhasePlan[],
  phaseCode: string,
  fn: (plan: EditorPhasePlan) => EditorPhasePlan
): EditorPhasePlan[] => {
  const found = plans.some((p) => p.phase_code === phaseCode);
  if (found) {
    return plans.map((p) => (p.phase_code === phaseCode ? fn(p) : p));
  }
  return [
    ...plans,
    fn({
      phase_code: phaseCode,
      duration_days: 0,
      start_date: "",
      end_date: "",
    }),
  ];
};

const mapTrade = (
  state: EditorState,
  tradeKey: string,
  fn: (trade: EditorTrade) => EditorTrade
): EditorState => ({
  ...state,
  dirty: true,
  trades: state.trades.map((t) => (t.key === tradeKey ? fn(t) : t)),
});

const mapRow = (
  items: EditorItem[],
  itemKey: string,
  fn: (item: EditorItem) => EditorItem
): EditorItem[] => items.map((i) => (i.key === itemKey ? fn(i) : i));

/** 자식이면 부모의 children 안에서, 아니면 공종의 items 안에서 바꾼다. */
const mapItem = (
  trade: EditorTrade,
  ref: ItemRef,
  fn: (item: EditorItem) => EditorItem
): EditorTrade => {
  if (!ref.parentKey) {
    return { ...trade, items: mapRow(trade.items, ref.itemKey, fn) };
  }
  return {
    ...trade,
    items: mapRow(trade.items, ref.parentKey, (parent) => ({
      ...parent,
      children: mapRow(parent.children, ref.itemKey, fn),
    })),
  };
};

const removeAt = (items: EditorItem[], itemKey: string): EditorItem[] =>
  items.filter((i) => i.key !== itemKey);

/** 제자리면 그대로 돌려준다 — 끝 행에서 화살표를 눌러도 dirty 가 되지 않게. */
const moveAt = (
  items: EditorItem[],
  itemKey: string,
  dir: -1 | 1
): EditorItem[] => {
  const from = items.findIndex((i) => i.key === itemKey);
  const to = from + dir;
  if (from < 0 || to < 0 || to >= items.length) return items;

  const next = items.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/** 값이 채워진 입력 한 줄을 편집기 행으로. 담기·붙여넣기·시트가 이 한 벌을 쓴다. */
export const rowFromFilled = (input: FilledItemInput): EditorItem => ({
  key: input.key ?? nextKey(),
  space_code: input.space_code,
  scope: input.scope,
  item: input.item,
  unit: input.unit,
  qty: input.qty,
  ...(input.qty_expr ? { qty_expr: input.qty_expr } : {}),
  material_unit_price: input.material_unit_price,
  labor_unit_price: input.labor_unit_price,
  // 단가표 제안의 원가·기준 마진 — 원가가 없으면 기준도 없다.
  ...(costOf(input.cost_unit_price) !== null
    ? { cost_unit_price: costOf(input.cost_unit_price), base_margin_bp: marginBpValue(input.base_margin_bp) }
    : {}),
  note: input.note,
  children: [],
  suggested: false,
});

/** 칸 값을 행에 얹는다. 수량만 바꾸면 식은 버리고, 단가를 바꾸면 제안 표시를 내린다(패치가 제안 표시를 정해 오면 그대로). */
export const applyItemPatch = (
  item: EditorItem,
  patch: EditorItemPatch
): EditorItem => {
  const next: EditorItem = { ...item, ...patch };
  if ("qty_expr" in patch && !patch.qty_expr) next.qty_expr = null;
  if (patch.qty !== undefined && !("qty_expr" in patch) && item.qty_expr) {
    next.qty_expr = null;
  }
  next.suggested =
    patch.suggested !== undefined
      ? patch.suggested
      : patch.material_unit_price !== undefined ||
          patch.labor_unit_price !== undefined
        ? false
        : item.suggested;
  return sameItem(item, next) ? item : next;
};

// 칸 값이 전부 같으면 같은 행이다. 식·원가·기준 마진은 undefined 와 null 을 같게 본다.
const sameItem = (a: EditorItem, b: EditorItem): boolean =>
  (Object.keys(b) as (keyof EditorItem)[]).every((k) =>
    k === "qty_expr" || k === "cost_unit_price" || k === "base_margin_bp"
      ? (a[k] ?? null) === (b[k] ?? null)
      : a[k] === b[k]
  );

/** 간단 모드 한 칸(자재+노무 합)을 나눈다. 노무는 두고 차액만 자재로 — 이유는 `SET_SIMPLE_PRICE` 주석. */
export const splitSimplePrice = (
  item: Pick<EditorItem, "labor_unit_price">,
  value: number
): { material_unit_price: number; labor_unit_price: number } => {
  const labor = value >= item.labor_unit_price ? item.labor_unit_price : 0;
  return { material_unit_price: value - labor, labor_unit_price: labor };
};

/** SET 부모에서 못 고치는 칸. 부모는 자식 합으로 계산하므로 자기 단위·수량·단가를 안 쓴다. */
export const SET_LOCKED_FIELDS: ReadonlyArray<keyof EditorItemPatch> = [
  "unit",
  "qty",
  "qty_expr",
  "material_unit_price",
  "labor_unit_price",
  "cost_unit_price",
  "base_margin_bp",
];

const withoutLocked = (patch: EditorItemPatch): EditorItemPatch => {
  const next: EditorItemPatch = { ...patch };
  for (const field of SET_LOCKED_FIELDS) delete next[field];
  return next;
};

const refKey = (ref: ItemRef): string =>
  `${ref.tradeKey}/${ref.parentKey ?? ""}/${ref.itemKey}`;

/** 좌표로 행을 찾는다. 없으면 null. */
export const findItem = (state: EditorState, ref: ItemRef): EditorItem | null => {
  const trade = state.trades.find((t) => t.key === ref.tradeKey);
  if (!trade) return null;
  if (!ref.parentKey) return trade.items.find((i) => i.key === ref.itemKey) ?? null;
  const parent = trade.items.find((i) => i.key === ref.parentKey);
  return parent?.children.find((c) => c.key === ref.itemKey) ?? null;
};

type ItemFn = (item: EditorItem) => EditorItem;

const addFn = (fns: Map<string, ItemFn>, ref: ItemRef, fn: ItemFn): void => {
  const key = refKey(ref);
  const prev = fns.get(key);
  fns.set(key, prev ? (item) => fn(prev(item)) : fn);
};

/** 좌표별 함수를 한 번에 적용한다. 안 바뀐 공종·행은 참조를 그대로 둔다. */
const updateItems = (
  state: EditorState,
  fns: Map<string, ItemFn>
): EditorState => {
  if (fns.size === 0) return state;
  let changed = false;
  const trades = state.trades.map((trade) => {
    let touched = false;
    const items = trade.items.map((item) => {
      const own = fns.get(`${trade.key}//${item.key}`);
      let next = own ? own(item) : item;
      let childTouched = false;
      const children = next.children.map((child) => {
        const fn = fns.get(`${trade.key}/${item.key}/${child.key}`);
        if (!fn) return child;
        const out = fn(child);
        if (out !== child) childTouched = true;
        return out;
      });
      if (childTouched) next = { ...next, children };
      if (next !== item) touched = true;
      return next;
    });
    if (!touched) return trade;
    changed = true;
    return { ...trade, items };
  });
  return changed ? { ...state, trades, dirty: true } : state;
};

const setCells = (state: EditorState, patches: CellPatch[]): EditorState => {
  const fns = new Map<string, ItemFn>();
  for (const { ref, patch } of patches) {
    addFn(fns, ref, (item) =>
      applyItemPatch(item, item.children.length > 0 ? withoutLocked(patch) : patch)
    );
  }
  return updateItems(state, fns);
};

const insertRows = (
  state: EditorState,
  tradeKey: string,
  index: number,
  inputs: FilledItemInput[]
): EditorState => {
  if (inputs.length === 0) return state;
  if (!state.trades.some((t) => t.key === tradeKey)) return state;
  return mapTrade(state, tradeKey, (trade) => {
    const at = Number.isFinite(index)
      ? Math.max(0, Math.min(Math.floor(index), trade.items.length))
      : trade.items.length;
    const items = trade.items.slice();
    items.splice(at, 0, ...inputs.map(rowFromFilled));
    return { ...trade, collapsed: false, items };
  });
};

const removeItems = (state: EditorState, refs: ItemRef[]): EditorState => {
  const gone = new Set(refs.map(refKey));
  if (gone.size === 0) return state;
  let changed = false;
  const trades = state.trades.map((trade) => {
    let touched = false;
    const items: EditorItem[] = [];
    for (const item of trade.items) {
      if (gone.has(`${trade.key}//${item.key}`)) {
        touched = true;
        continue;
      }
      const children = item.children.filter(
        (c) => !gone.has(`${trade.key}/${item.key}/${c.key}`)
      );
      if (children.length !== item.children.length) {
        touched = true;
        items.push({ ...item, children });
      } else {
        items.push(item);
      }
    }
    if (!touched) return trade;
    changed = true;
    return { ...trade, items };
  });
  return changed ? { ...state, trades, dirty: true } : state;
};

const fillDown = (
  state: EditorState,
  refs: ItemRef[],
  fields: FillField[]
): EditorState => {
  const [first, ...rest] = refs;
  const source = first ? findItem(state, first) : null;
  if (!source || rest.length === 0 || fields.length === 0) return state;

  const patch: EditorItemPatch = {};
  for (const field of fields) {
    if (field === "qty") {
      patch.qty = source.qty;
      patch.qty_expr = source.qty_expr ?? null;
    } else if (field === "material_unit_price" || field === "labor_unit_price") {
      patch[field] = source[field];
    } else if (field === "cost_unit_price") {
      // 기준 마진도 같이 — 원가만 오면 받은 줄의 계산값이 원본과 갈린다.
      patch.cost_unit_price = source.cost_unit_price ?? null;
      patch.base_margin_bp = source.base_margin_bp ?? null;
    } else {
      patch[field] = source[field];
    }
  }
  const usable = source.children.length > 0 ? withoutLocked(patch) : patch;
  if (Object.keys(usable).length === 0) return state;

  const sourceKey = refKey(first);
  const fns = new Map<string, ItemFn>();
  for (const ref of rest) {
    if (refKey(ref) === sourceKey) continue;
    addFn(fns, ref, (item) =>
      applyItemPatch(item, item.children.length > 0 ? withoutLocked(usable) : usable)
    );
  }
  return updateItems(state, fns);
};

/** 기본 반올림 단위. 단가표·견적서가 100원 아래를 안 쓰는 관행에 맞춘다. */
export const PRICE_ROUND_UNIT = 100;

const scalePrices = (
  state: EditorState,
  refs: ItemRef[],
  ratio: number,
  unit: number | undefined
): EditorState => {
  if (!Number.isFinite(ratio) || ratio < 0) return state;
  const step =
    unit !== undefined && Number.isFinite(unit) && unit >= 1
      ? Math.floor(unit)
      : PRICE_ROUND_UNIT;
  const scale = (price: number): number =>
    Math.round((price * ratio) / step) * step;

  const fns = new Map<string, ItemFn>();
  for (const ref of refs) {
    addFn(fns, ref, (item) => {
      if (item.children.length > 0) return item;
      const material = scale(item.material_unit_price);
      const labor = scale(item.labor_unit_price);
      if (material === item.material_unit_price && labor === item.labor_unit_price) {
        return item;
      }
      return applyItemPatch(item, {
        material_unit_price: material,
        labor_unit_price: labor,
      });
    });
  }
  return updateItems(state, fns);
};

// 면적을 바꾸면 식이 있는 수량을 다시 계산한다 — 평·m² 짝은 부르는 쪽이 맞춰 준다.
const setArea = (state: EditorState, area: number | null, areaM2: number | null): EditorState => {
  const fns = new Map<string, ItemFn>();
  const recalc: ItemFn = (item) => {
    if (!item.qty_expr || item.children.length > 0) return item;
    const result = evalQtyExpr(item.qty_expr, { area_pyeong: area, area_m2: areaM2 });
    // 평수를 지우면 식은 두고 수량은 0 — 옛 평수로 낸 수량이 금액에 남지 않게. 그 밖의 식 오류는 마지막 값을 둔다.
    const qty = result.ok ? result.value : result.kind === "no_area" ? 0 : item.qty;
    if (qty === item.qty) return item;
    return { ...item, qty };
  };
  for (const trade of state.trades) {
    for (const item of trade.items) {
      if (item.qty_expr && item.children.length === 0) {
        addFn(fns, { tradeKey: trade.key, itemKey: item.key, parentKey: null }, recalc);
      }
      for (const child of item.children) {
        if (child.qty_expr) {
          addFn(fns, { tradeKey: trade.key, itemKey: child.key, parentKey: item.key }, recalc);
        }
      }
    }
  }
  const next = updateItems(state, fns);
  if (next === state && area === state.area_pyeong && areaM2 === stateAreaM2(state)) return state;
  return { ...next, area_pyeong: area, area_m2: areaM2, dirty: true };
};

const setAreaPyeong = (state: EditorState, value: number | null): EditorState => {
  const area = areaOf(value);
  return setArea(state, area, pyeongToM2(area));
};

// m² 입력이 원본 — 평은 m²/3.305785 를 둘째 자리로(#173).
const setAreaM2 = (state: EditorState, value: number | null): EditorState => {
  const m2 = areaOf(value);
  const pyeong = m2ToPyeong(m2);
  return setArea(state, pyeong, pyeong === null ? null : m2);
};

/**
 * 값이 채워진 항목을 공종 맨 뒤에 붙인다.
 *
 * 맨 뒤인 이유 — 「빠른 추가」 는 위에서 넣는데 결과는 아래에 쌓여야 방금 넣은
 * 것이 어디 갔는지 눈으로 따라갈 수 있다. 가운데 끼워 넣으면 읽던 자리를 잃는다.
 *
 * **비어 있던 기본 줄은 치운다.** 새 공종은 빈 줄 하나를 달고 태어나는데, 거기에
 * 담기를 하면 빈 줄과 새 줄이 나란히 남아 표가 지저분해진다.
 */
const addFilled = (
  state: EditorState,
  tradeKey: string,
  inputs: FilledItemInput[]
): EditorState => {
  if (inputs.length === 0) return state;

  const rows = inputs.map(rowFromFilled);

  return {
    ...state,
    dirty: true,
    trades: state.trades.map((trade) =>
      trade.key === tradeKey
        ? {
            ...trade,
            collapsed: false,
            items: [...trade.items.filter((i) => !isBlank(i)), ...rows],
          }
        : trade
    ),
  };
};

interface AddTradeInput {
  code: TradeCode;
  name: string;
  group: TradeGroup;
  sort_order: number;
}

const addTrade = (state: EditorState, input: AddTradeInput): EditorState => {
  if (state.trades.some((t) => t.trade === input.code)) return state;

  const row: EditorTrade = {
    key: nextKey(),
    group: input.group,
    trade: input.code,
    name: input.name,
    sort_order: input.sort_order,
    condition_text: "",
    collapsed: false,
    items: [emptyItem()],
  };

  // 공종 순서는 공종 표가 정한 자리를 따른다 — 사람이 끌어 옮기지 않는다.
  // 내장 상수에서 찾지 않는 이유는 `EditorTrade.sort_order` 주석에 있다.
  const trades = [...state.trades, row].sort(
    (a, b) => a.sort_order - b.sort_order
  );
  return { ...state, trades, dirty: true };
};

// D4 배너 문구 — 평수가 없으면 「평수 기준」 대신 평수 입력을 안내한다(평 식은 0 으로 들어간다).
export const fillTemplateNoteOf = (area: number | null): string =>
  area === null
    ? "항목이 비어 있습니다 — 기본 템플릿의 공종별 항목을 넣을 수 있습니다. 평수를 입력하면 평 기준 수량이 함께 채워집니다."
    : `항목이 비어 있습니다 — 기본 템플릿의 공종별 항목을 ${area}평 기준 수량과 함께 넣을 수 있습니다.`;

// 「기본 템플릿 항목 한 번에 넣기」 를 보일지 — 모든 줄이 빈 줄이고, 공종 코드가 맞는 템플릿 항목이 하나라도 있을 때.
export const canFillTemplate = (
  state: EditorState,
  byTrade: ReadonlyMap<string, TemplateItem[]>
): boolean =>
  state.trades.every((t) => t.items.every(isBlank)) &&
  state.trades.some((t) => (byTrade.get(t.trade) ?? []).length > 0);

// 템플릿 항목이 있는 공종만 그 항목으로 갈아 끼운다(빈 줄뿐이라 버릴 것이 없다) — 넣을 수 없으면 상태 그대로.
const fillTemplate = (state: EditorState, byTrade: ReadonlyMap<string, TemplateItem[]>): EditorState => {
  if (!canFillTemplate(state, byTrade)) return state;
  const trades = state.trades.map((t) => {
    const items = byTrade.get(t.trade) ?? [];
    return items.length === 0
      ? t
      : { ...t, collapsed: false, items: templateItemsToRows(items, state.area_pyeong, stateAreaM2(state)) };
  });
  return { ...state, trades, dirty: true };
};

// 가져온 공종들을 담는다. 같은 코드가 있으면 그 공종 맨 뒤에(기존 줄은 그대로), 없으면 새 공종으로.
const importTrades = (state: EditorState, inputs: ImportTradeInput[]): EditorState => {
  let next = state;
  for (const input of inputs) {
    if (input.items.length === 0) continue;
    const existing = next.trades.find((t) => t.trade === input.trade);
    if (existing) {
      next = addFilled(next, existing.key, input.items);
      continue;
    }
    const row: EditorTrade = {
      key: nextKey(),
      group: input.group,
      trade: input.trade,
      name: input.name,
      sort_order: input.sort_order,
      condition_text: "",
      collapsed: false,
      items: input.items.map(rowFromFilled),
    };
    next = {
      ...next,
      dirty: true,
      trades: [...next.trades, row].sort((a, b) => a.sort_order - b.sort_order),
    };
  }
  return next;
};

// 원가 한 칸 — 0 이상 안전정수 또는 null 만 받는다.
const setCost = (state: EditorState, ref: ItemRef, value: number | null): EditorState => {
  if (value !== null && !(Number.isSafeInteger(value) && value >= 0)) return state;
  return updateItems(
    state,
    new Map([
      [
        refKey(ref),
        (item: EditorItem) =>
          item.children.length > 0 ? item : applyItemPatch(item, { cost_unit_price: value }),
      ],
    ])
  );
};

// 마진 → 판매 단가(자재·노무는 지금 비율로) · 기준 마진도 이 값으로 — 만분율로 맞춘 뒤 계산해 계산값과 판매가가 같다.
const marginPatch = (item: EditorItem, margin: number, mode: VatMode): EditorItem => {
  if (!marginEditable(item) || !Number.isFinite(margin)) return item;
  const bp = Math.round(margin * 10_000);
  if (!isMarginBp(bp)) return item;
  let sale: number;
  try {
    sale = saleFromMargin(item.cost_unit_price ?? 0, bp / 10_000, mode);
  } catch {
    return item;
  }
  return applyItemPatch(item, { ...splitByRow(item, sale), base_margin_bp: bp });
};

const setMargin = (state: EditorState, ref: ItemRef, margin: number): EditorState => {
  const mode = vatModeOf(state);
  return updateItems(state, new Map([[refKey(ref), (item: EditorItem) => marginPatch(item, margin, mode)]]));
};

// 직접 입력한 판매가 → 계산값(비율 유지) · 계산값이 없거나 같으면 그대로.
const resetToCalc = (state: EditorState, ref: ItemRef): EditorState => {
  const mode = vatModeOf(state);
  return updateItems(
    state,
    new Map([
      [
        refKey(ref),
        (item: EditorItem) => {
          const calc = calcSaleOf(item, mode);
          return calc === null || calc === saleUnitOf(item) ? item : applyItemPatch(item, splitByRow(item, calc));
        },
      ],
    ])
  );
};

// 공종 일괄 마진 — 원가 있는 잎(SET 자식 포함)마다 같은 마진 · 원가 없는 줄은 그대로.
const setTradeMargin = (state: EditorState, tradeKey: string, margin: number): EditorState => {
  const trade = state.trades.find((t) => t.key === tradeKey);
  if (!trade) return state;
  const mode = vatModeOf(state);
  const fns = new Map<string, ItemFn>();
  const fn: ItemFn = (item) => marginPatch(item, margin, mode);
  for (const item of trade.items) {
    if (item.children.length === 0) {
      fns.set(refKey({ tradeKey, itemKey: item.key, parentKey: null }), fn);
      continue;
    }
    for (const child of item.children) fns.set(refKey({ tradeKey, itemKey: child.key, parentKey: item.key }), fn);
  }
  return updateItems(state, fns);
};

// 부가세 방식 전환 — 잎의 자재·노무 단가를 각각 변환(원가 그대로) · 손으로 넣은 절사는 자동으로 되돌린다 · 하나라도 못 바꾸면 그대로.
const setVatMode = (state: EditorState, to: VatMode, status: QuoteStatus): EditorState => {
  const from = vatModeOf(state);
  if (!vatModeSwitchable(status) || from === to) return state;
  const convert = (item: EditorItem): EditorItem => {
    if (item.children.length > 0) {
      const children = item.children.map(convert);
      return children.every((c, i) => c === item.children[i]) ? item : { ...item, children };
    }
    // 계산값 그대로인 줄(#198 이전 원 단위 계산값 포함)은 새 방식의 계산값으로 — 반올림 1원 차이로 「직접 입력」 이 뜨지 않게.
    if (calcSaleOf(item, from) !== null && !isSaleOverridden(item, from)) {
      const calc = calcSaleOf(item, to);
      if (calc !== null) return calc === saleUnitOf(item) ? item : { ...item, ...splitByRow(item, calc) };
    }
    const material = convertUnitPrice(item.material_unit_price, from, to);
    const labor = convertUnitPrice(item.labor_unit_price, from, to);
    if (material === item.material_unit_price && labor === item.labor_unit_price) return item;
    // 제안 표시는 그대로 — 같은 제안을 다른 방식으로 적은 값이다.
    return { ...item, material_unit_price: material, labor_unit_price: labor };
  };
  let trades: EditorTrade[];
  try {
    trades = state.trades.map((trade) => {
      const items = trade.items.map(convert);
      return items.every((it, i) => it === trade.items[i]) ? trade : { ...trade, items };
    });
  } catch {
    return state;
  }
  return { ...state, trades, vat_mode: to, rounding: null, dirty: true };
};

export const editorReducer = (
  state: EditorState,
  action: EditorAction
): EditorState => {
  switch (action.type) {
    case "SET_TITLE":
      return { ...state, title: action.value, dirty: true };

    case "SET_MGMT_PCT":
      return { ...state, mgmt_fee_pct: action.value, dirty: true };

    case "SET_ROUNDING":
      return { ...state, rounding: action.value, dirty: true };

    case "SET_EXTRA_NOTICE":
      return { ...state, extra_notice: action.value, dirty: true };

    case "ADD_CONDITION":
      return {
        ...state,
        conditions: [...state.conditions, ""],
        condition_keys: [...state.condition_keys, nextKey()],
        dirty: true,
      };

    case "SET_CONDITION":
      return {
        ...state,
        dirty: true,
        conditions: state.conditions.map((c, i) =>
          i === action.index ? action.value : c
        ),
      };

    case "REMOVE_CONDITION":
      return {
        ...state,
        dirty: true,
        conditions: state.conditions.filter((_, i) => i !== action.index),
        condition_keys: state.condition_keys.filter(
          (_, i) => i !== action.index
        ),
      };

    case "TOGGLE_TRADE":
      // 접기는 편집이 아니다 — dirty 를 올리지 않는다.
      return {
        ...state,
        trades: state.trades.map((t) =>
          t.key === action.tradeKey ? { ...t, collapsed: !t.collapsed } : t
        ),
      };

    case "SET_TRADE_NAME":
      return mapTrade(state, action.tradeKey, (t) => ({
        ...t,
        name: action.value,
      }));

    case "SET_TRADE_CONDITION":
      return mapTrade(state, action.tradeKey, (t) => ({
        ...t,
        condition_text: action.value,
      }));

    case "ADD_TRADE":
      return addTrade(state, {
        code: action.trade,
        name: action.name,
        group: action.group,
        sort_order: action.sort_order,
      });

    case "REMOVE_TRADE":
      return {
        ...state,
        dirty: true,
        trades: state.trades.filter((t) => t.key !== action.tradeKey),
      };

    case "ADD_ITEM":
      return mapTrade(state, action.tradeKey, (t) => ({
        ...t,
        collapsed: false,
        items: [...t.items, emptyItem()],
      }));

    case "ADD_TEMPLATE_ITEMS":
      return mapTrade(state, action.tradeKey, (t) => ({
        ...t,
        collapsed: false,
        items: [...t.items, ...templateItemsToRows(action.items, state.area_pyeong, stateAreaM2(state))],
      }));

    case "FILL_TEMPLATE":
      return fillTemplate(state, action.byTrade);

    case "ADD_CHILD":
      // SET 부모가 되는 순간 자기 수량·단가는 금액에 안 쓰인다. 남겨 두면
      // 화면에는 안 보이는 옛 단가가 저장·스냅샷까지 따라간다.
      return mapTrade(state, action.tradeKey, (t) => ({
        ...t,
        items: mapRow(t.items, action.parentKey, (parent) => ({
          ...parent,
          qty: 0,
          qty_expr: null,
          material_unit_price: 0,
          labor_unit_price: 0,
          suggested: false,
          children: [...parent.children, emptyItem()],
        })),
      }));

    case "REMOVE_ITEM":
      return mapTrade(state, action.ref.tradeKey, (t) => {
        if (!action.ref.parentKey) {
          return { ...t, items: removeAt(t.items, action.ref.itemKey) };
        }
        return {
          ...t,
          items: mapRow(t.items, action.ref.parentKey, (parent) => ({
            ...parent,
            children: removeAt(parent.children, action.ref.itemKey),
          })),
        };
      });

    case "MOVE_ITEM": {
      // 제자리 이동(맨 윗줄 올리기 등)은 상태 그대로 — dirty·되돌리기 단계가 안 생긴다.
      const trade = state.trades.find((t) => t.key === action.ref.tradeKey);
      const list = !trade
        ? null
        : action.ref.parentKey
          ? trade.items.find((i) => i.key === action.ref.parentKey)?.children ?? null
          : trade.items;
      if (!list || moveAt(list, action.ref.itemKey, action.dir) === list) return state;
      return mapTrade(state, action.ref.tradeKey, (t) => {
        if (!action.ref.parentKey) {
          return { ...t, items: moveAt(t.items, action.ref.itemKey, action.dir) };
        }
        return {
          ...t,
          items: mapRow(t.items, action.ref.parentKey, (parent) => ({
            ...parent,
            children: moveAt(parent.children, action.ref.itemKey, action.dir),
          })),
        };
      });
    }

    case "SET_ITEM":
      // 사람이 단가를 고치면 제안값 표시는 내린다(`applyItemPatch`).
      // 같은 값이면 상태 그대로 — dirty·되돌리기 단계가 안 생긴다.
      return updateItems(
        state,
        new Map([[refKey(action.ref), (item: EditorItem) => applyItemPatch(item, action.patch)]])
      );

    case "SUGGEST_PRICE":
      // 제안은 서버 왕복 뒤에 도착한다. 그 사이 사람이 단가를 쳤으면 그쪽이 이긴다.
      // **그 사이 SET 자식이 생겼으면 그쪽도 이긴다** — 부모는 자식 합으로 계산해
      // 금액에는 안 들어가지만, 잠긴 칸에 단가가 보이고 스냅샷에 남는다.
      return mapTrade(state, action.ref.tradeKey, (t) =>
        mapItem(t, action.ref, (item) =>
          item.children.length > 0 ||
          item.material_unit_price > 0 ||
          item.labor_unit_price > 0
            ? item
            : {
                ...item,
                material_unit_price: action.material_unit_price,
                labor_unit_price: action.labor_unit_price,
                // 사람이 친 원가가 있으면 그 원가·기준이 이긴다.
                ...((item.cost_unit_price ?? null) === null && costOf(action.cost_unit_price) !== null
                  ? { cost_unit_price: costOf(action.cost_unit_price), base_margin_bp: marginBpValue(action.base_margin_bp) }
                  : {}),
                suggested: true,
              }
        )
      );

    case "SET_SIMPLE_PRICE":
      /**
       * 간단 모드의 칸은 **자재 + 노무 합**이다. 넣은 값이 곧 합계가 되어야 한다.
       *
       * 노무를 0 으로 밀지 않는 이유 — 발송하면 `upsertPrices` 가 이 줄로 단가표를
       * 덮는다. 상세 모드에서 40,000 / 20,000 으로 쌓아 둔 줄을 간단 모드에서
       * 65,000 으로 고치면, 노무를 비우는 순간 **단가표의 분리 이력이 영영
       * 사라져** 다음부터 동료의 상세 모드 제안이 노무 0 으로 나온다.
       *
       * 그래서 **노무는 그대로 두고 차액만 자재에 넣는다.** 합계는 넣은 값과 정확히
       * 같고, 나눠 둔 비율의 뿌리는 남는다. 넣은 값이 노무보다 작으면 나눌 수가
       * 없으니 그때만 한 칸으로 합친다.
       */
      // 같은 값이면 상태 그대로(`applyItemPatch` 가 단가를 받으면 제안 표시도 내린다).
      return updateItems(
        state,
        new Map([
          [
            refKey(action.ref),
            (item: EditorItem) => applyItemPatch(item, splitSimplePrice(item, action.value)),
          ],
        ])
      );

    case "SET_COST":
      return setCost(state, action.ref, action.value);

    case "SET_MARGIN":
      return setMargin(state, action.ref, action.margin);

    case "RESET_TO_CALC":
      return resetToCalc(state, action.ref);

    case "SET_TRADE_MARGIN":
      return setTradeMargin(state, action.tradeKey, action.margin);

    case "SET_VAT_MODE":
      return setVatMode(state, action.value, action.status);

    case "ADD_FILLED_ITEM":
      return addFilled(state, action.tradeKey, [action.item]);

    case "ADD_FILLED_ITEMS":
      return addFilled(state, action.tradeKey, action.items);

    case "SET_SCHEDULE_START":
      return withSchedule(state, { expected_start_date: action.value });

    case "TOGGLE_WORKDAY":
      return withSchedule(state, {
        [action.field]: !state.schedule[action.field],
      });

    case "SET_PHASE_DURATION":
      return withSchedule(state, {
        plans: upsertPlan(state.schedule.plans, action.phase_code, (plan) => {
          const days = Math.max(0, Math.floor(action.value) || 0);
          // 손으로 일수를 바꾸면 초안 근거는 더는 맞지 않는다 — 고른 시공법만 남긴다.
          return days === plan.duration_days ? plan : { ...plan, duration_days: days, quantity_m2: null, crew_size: null, draft_days: null };
        }),
      });

    case "SET_PHASE_METHOD":
      return withSchedule(state, {
        plans: upsertPlan(state.schedule.plans, action.phase_code, (plan) =>
          plan.standard_item_code === action.item_code ? plan : { ...plan, standard_item_code: action.item_code, quantity_m2: null, crew_size: null, draft_days: null }
        ),
      });

    case "APPLY_PHASE_DRAFTS": {
      let plans = state.schedule.plans;
      for (const d of action.drafts) {
        plans = upsertPlan(plans, d.phase_code, (plan) => ({
          ...plan,
          duration_days: d.duration_days,
          standard_item_code: d.standard_item_code,
          quantity_m2: d.quantity_m2,
          crew_size: d.crew_size,
          draft_days: d.draft_days,
        }));
      }
      return withSchedule(state, { plans, process_standard_version_id: action.version_id });
    }

    case "SET_PHASE_DATE":
      return withSchedule(state, {
        plans: upsertPlan(state.schedule.plans, action.phase_code, (plan) => ({
          ...plan,
          [action.field]: action.value,
        })),
      });

    case "APPLY_SCHEDULE": {
      const byCode = new Map(action.phases.map((p) => [p.phase_code, p]));
      return withSchedule(state, {
        holidays: action.holidays,
        plans: state.schedule.plans.map((plan) => {
          const next = byCode.get(plan.phase_code);
          // 이번 초안에 안 낀 공정(며칠이 비었다)은 날짜를 비운다 — 옛 날짜가
          // 남아 있으면 안 쓰기로 한 공정이 견적서 공정표에 그대로 선다.
          return next
            ? { ...plan, start_date: next.start_date, end_date: next.end_date }
            : { ...plan, start_date: "", end_date: "" };
        }),
      });
    }

    case "SET_CELLS": {
      const patched = setCells(state, action.patches);
      return action.insert
        ? insertRows(patched, action.insert.tradeKey, action.insert.index, action.insert.items)
        : patched;
    }

    case "INSERT_ROWS_AT":
      return insertRows(state, action.tradeKey, action.index, action.items);

    case "REMOVE_ITEMS":
      return removeItems(state, action.refs);

    case "FILL_DOWN":
      return fillDown(state, action.refs, action.fields);

    case "SCALE_PRICES":
      return scalePrices(state, action.refs, action.ratio, action.unit);

    case "IMPORT_TRADES":
      return importTrades(state, action.trades);

    case "SET_AREA_PYEONG":
      return setAreaPyeong(state, action.value);

    case "SET_AREA_M2":
      return setAreaM2(state, action.value);

    case "SAVED":
      // 절사 입력은 건드리지 않는다 — 「자동」으로 둔 것을 저장이 고정값으로 굳히면
      // 다음 수정부터 만원 단위가 안 맞는다. 화면에는 saved 를 보여 준다.
      return { ...state, dirty: false, saved: action.amounts };

    case "RESTORE":
      // 되살린 편집은 아직 본문에 안 들어갔다 — 저장해야 할 상태로 둔다.
      // 키는 **전부 새로 붙인다** — 자세한 이유는 `rekey` 주석에.
      return { ...rekey(action.state), dirty: true };

    default:
      return state;
  }
};
